import { jsPDF } from "jspdf"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SvhcTableRow = {
  productId: string
  productName: string
  casNumber: string
  substanceName: string
  svhcGehalt: string
  locationInComponent: string
}

export type ProductGroup = {
  productId: string
  productName: string
  rows: SvhcTableRow[]
}

export type ConformityFormData = {
  issuer: string
  recipient: string
  issuerAddress: string
  creationDate: string
  declarationText: string
  regulatoryNotes: string
  signatory: string
  signatoryRole: string
  signatoryPlace: string
  signatoryDate: string
}

// ---------------------------------------------------------------------------
// Constants (German REACH boilerplate)
// ---------------------------------------------------------------------------

const CANDIDATE_LIST_VERSION = "31. Aktualisierung"
const CANDIDATE_LIST_DATE = "2024-06-27"
const CANDIDATE_LIST_ENTRIES = 240

export function generatePositiveText(
  customer: string,
  svhcCount: number,
): string {
  return `Hiermit erkl\u00E4ren wir, dass die nachfolgend aufgef\u00FChrten ${svhcCount} Artikel, die an ${customer} geliefert werden, Substanzen der REACH-Kandidatenliste (${CANDIDATE_LIST_VERSION}, ${CANDIDATE_LIST_DATE}) in einer Konzentration von mehr als 0,1 Gew.-% enthalten. Die Mitteilungspflicht gem\u00E4\u00DF REACH Art. 33 Abs. 1 wird hiermit erf\u00FCllt.`
}

export function generateNegativeText(
  customer: string,
  totalCount: number,
): string {
  return `Hiermit erkl\u00E4ren wir, dass keiner der ${totalCount} Artikel, die an ${customer} geliefert werden, Substanzen der REACH-Kandidatenliste in einer Konzentration von mehr als 0,1 Gew.-% enth\u00E4lt. Eine Mitteilungspflicht gem\u00E4\u00DF REACH Art. 33 Abs. 1 besteht daher nicht.`
}

export function generateProductPositiveText(
  productName: string,
  svhcCount: number,
): string {
  return `Hiermit erkl\u00E4ren wir, dass das Produkt \u201E${productName}\u201C ${svhcCount} Substanz${svhcCount !== 1 ? "en" : ""} der REACH-Kandidatenliste (${CANDIDATE_LIST_VERSION}, ${CANDIDATE_LIST_DATE}) in einer Konzentration von mehr als 0,1 Gew.-% enth\u00E4lt. Die nachfolgende Tabelle listet die betroffenen Stoffe auf. Die Mitteilungspflicht gem\u00E4\u00DF REACH Art. 33 Abs. 1 wird hiermit erf\u00FCllt.`
}

export function generateProductNegativeText(productName: string): string {
  return `Hiermit erkl\u00E4ren wir, dass das Produkt \u201E${productName}\u201C keine Substanzen der REACH-Kandidatenliste in einer Konzentration von mehr als 0,1 Gew.-% enth\u00E4lt. Eine Mitteilungspflicht gem\u00E4\u00DF REACH Art. 33 Abs. 1 besteht daher nicht.`
}

export function generateRegulatoryNotes(): string {
  return "REACH (EG) Nr. 1907/2006, Art. 33 \u2014 Mitteilungspflicht erf\u00FCllt\nRoHS 2011/65/EU \u2014 gepr\u00FCft gegen aktuelle Ausnahmeliste (Anhang III/IV)\nVerpackungsrichtlinie 2004/12/EG \u2014 alle Verpackungsmaterialien unter 100 ppm Schwermetalle, konform\nSCIP-Datenbank \u2014 meldepflichtige Artikel mit SCIP-IDs erfasst"
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

// LABELS is consumed by the conformity modal UI (not the PDF body).
// Keys map to QMS/CONFORMITY/* translation keys \u2014 resolved at call sites.
export const LABEL_KEYS = {
  modalTitle: "QMS/CONFORMITY/MODAL_TITLE",
  sectionLetterhead: "QMS/CONFORMITY/SECTION_LETTERHEAD",
  fieldIssuer: "QMS/CONFORMITY/FIELD_ISSUER",
  fieldRecipient: "QMS/CONFORMITY/FIELD_RECIPIENT",
  fieldIssuerAddress: "QMS/CONFORMITY/FIELD_ISSUER_ADDRESS",
  fieldCreationDate: "QMS/CONFORMITY/FIELD_CREATION_DATE",
  sectionDeclarationText: "QMS/CONFORMITY/SECTION_DECLARATION_TEXT",
  sectionSvhcTable: "QMS/CONFORMITY/SECTION_SVHC_TABLE",
  svhcTableNote: "QMS/CONFORMITY/SVHC_TABLE_NOTE",
  negativeSvhcNote: "QMS/CONFORMITY/NEGATIVE_SVHC_NOTE",
  sectionRegulatoryNotes: "QMS/CONFORMITY/SECTION_REGULATORY_NOTES",
  sectionSignature: "QMS/CONFORMITY/SECTION_SIGNATURE",
  fieldSignatory: "QMS/CONFORMITY/FIELD_SIGNATORY",
  fieldRole: "QMS/CONFORMITY/FIELD_ROLE",
  fieldPlace: "QMS/CONFORMITY/FIELD_PLACE",
  fieldDate: "QMS/CONFORMITY/FIELD_DATE",
  cancelButton: "QMS/COMMON/CANCEL",
  downloadButton: "QMS/CONFORMITY/DOWNLOAD_BUTTON",
  selectCustomers: "QMS/CONFORMITY/SELECT_CUSTOMERS",
  selectAll: "QMS/CONFORMITY/SELECT_ALL",
  deselectAll: "QMS/CONFORMITY/DESELECT_ALL",
  continueButton: "QMS/CONFORMITY/CONTINUE_BUTTON",
  searchCustomers: "QMS/CONFORMITY/SEARCH_CUSTOMERS",
  backButton: "QMS/CONFORMITY/BACK_BUTTON",
} as const

// PDF column headers \u2014 still used in doc.text() calls inside the PDF body.
// Kept as German because these appear in PDF documents destined for German customers.
export const LABELS = {
  colProductId: "Produkt-ID",
  colProductName: "Produktname",
  colCasNo: "CAS-Nr.",
  colSubstance: "SVHC-Stoff",
  colShare: "Gehalt (%)",
  colLocation: "Ort im Bauteil",
}

// ---------------------------------------------------------------------------
// PDF generation
// ---------------------------------------------------------------------------

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, "_")
}

export function generateConformityPdf(
  form: ConformityFormData,
  customer: string,
  svhcRows: SvhcTableRow[],
  totalProducts: number,
  isPositive: boolean,
  productGroups?: ProductGroup[],
) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentW = pageW - 2 * margin
  let y = margin

  const addFooter = () => {
    const page = doc.getNumberOfPages()
    for (let i = 1; i <= page; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `turnus.ai QMS \u00B7 Vertraulich \u00B7 Seite ${i} von ${page}`,
        margin,
        pageH - 10,
      )
      doc.text(
        `REACH-Konformit\u00E4tserkl\u00E4rung \u00B7 ${customer} \u00B7 ${form.creationDate}`,
        pageW - margin,
        pageH - 10,
        { align: "right" },
      )
    }
  }

  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - 20) {
      doc.addPage()
      y = margin
    }
  }

  // --- 1. Header block ---
  doc.setFontSize(18)
  doc.setTextColor(30)
  doc.setFont("helvetica", "bold")
  doc.text("Konformit\u00E4tserkl\u00E4rung REACH", margin, y)
  y += 7
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(100)
  doc.text(
    "gem\u00E4\u00DF Verordnung (EG) Nr. 1907/2006 (REACH), Artikel 33",
    margin,
    y,
  )
  y += 4
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 2. Meta block (two columns) ---
  doc.setFontSize(8)
  doc.setTextColor(120)
  const col1X = margin
  const col2X = margin + contentW / 2 + 5
  const metaLineH = 5

  const metaLeft = [
    ["Aussteller", form.issuer],
    ["Adresse", form.issuerAddress],
    ["Erstellungsdatum", form.creationDate],
  ]
  const metaRight = [
    ["Empf\u00E4nger", form.recipient],
    ["Produkte gesamt", String(totalProducts)],
    [
      "Kandidatenliste",
      `${CANDIDATE_LIST_VERSION} (${CANDIDATE_LIST_ENTRIES} Eintr\u00E4ge, ${CANDIDATE_LIST_DATE})`,
    ],
  ]

  for (let i = 0; i < 3; i++) {
    doc.setFont("helvetica", "normal")
    doc.setTextColor(120)
    doc.text(metaLeft[i][0], col1X, y)
    doc.text(metaRight[i][0], col2X, y)
    y += metaLineH * 0.6
    doc.setFont("helvetica", "bold")
    doc.setTextColor(50)
    doc.text(metaLeft[i][1], col1X, y)
    doc.text(metaRight[i][1], col2X, y)
    y += metaLineH
  }

  y += 2
  doc.setDrawColor(200)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 3. Declaration text ---
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Erkl\u00E4rung", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  const declLines = doc.splitTextToSize(form.declarationText, contentW)
  for (const line of declLines) {
    ensureSpace(5)
    doc.text(line, margin, y)
    y += 4.5
  }
  y += 6

  // --- 4. Per-product SVHC tables (positive only) ---
  if (isPositive && productGroups && productGroups.length > 0) {
    const svhcProducts = productGroups.filter((pg) => pg.rows.length > 0)
    for (const pg of svhcProducts) {
      // Product header
      ensureSpace(28)
      doc.setFillColor(235, 235, 240)
      doc.rect(margin, y - 4, contentW, 16, "F")
      doc.setFontSize(9)
      doc.setFont("helvetica", "bold")
      doc.setTextColor(30)
      doc.text(`${pg.productId}  —  ${pg.productName}`, margin + 3, y)
      y += 10

      // SVHC table for this product
      const colWidths = [38, 52, 38, 42]
      const headers = [
        LABELS.colCasNo,
        LABELS.colSubstance,
        LABELS.colShare,
        LABELS.colLocation,
      ]

      // Table header row
      doc.setFillColor(45, 45, 50)
      doc.rect(margin, y - 4, contentW, 7, "F")
      doc.setFontSize(7)
      doc.setFont("helvetica", "bold")
      doc.setTextColor(255)
      let cx = margin + 2
      for (let i = 0; i < headers.length; i++) {
        doc.text(headers[i], cx, y)
        cx += colWidths[i]
      }
      y += 5

      // Data rows
      doc.setFont("helvetica", "normal")
      doc.setFontSize(7.5)
      pg.rows.forEach((entry, idx) => {
        ensureSpace(8)
        if (idx % 2 === 0) {
          doc.setFillColor(245, 245, 248)
          doc.rect(margin, y - 3.5, contentW, 6, "F")
        }
        doc.setTextColor(60)
        cx = margin + 2
        const vals = [
          entry.casNumber,
          entry.substanceName,
          entry.svhcGehalt,
          entry.locationInComponent,
        ]
        for (let i = 0; i < vals.length; i++) {
          const truncated =
            doc.splitTextToSize(vals[i], colWidths[i] - 3)[0] ?? vals[i]
          doc.text(truncated, cx, y)
          cx += colWidths[i]
        }
        y += 6
      })
      y += 6
    }
  } else if (isPositive && svhcRows.length > 0) {
    // Fallback: flat table if no product groups provided
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(30)
    doc.text("SVHC-Stofftabelle", margin, y)
    y += 8

    const colWidths = [24, 32, 24, 34, 22, 34]
    const headers = [
      LABELS.colProductId,
      LABELS.colProductName,
      LABELS.colCasNo,
      LABELS.colSubstance,
      LABELS.colShare,
      LABELS.colLocation,
    ]

    doc.setFillColor(45, 45, 50)
    doc.rect(margin, y - 4, contentW, 7, "F")
    doc.setFontSize(7)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(255)
    let cx = margin + 2
    for (let i = 0; i < headers.length; i++) {
      doc.text(headers[i], cx, y)
      cx += colWidths[i]
    }
    y += 5

    doc.setFont("helvetica", "normal")
    doc.setFontSize(7.5)
    svhcRows.forEach((entry, idx) => {
      ensureSpace(8)
      if (idx % 2 === 0) {
        doc.setFillColor(245, 245, 248)
        doc.rect(margin, y - 3.5, contentW, 6, "F")
      }
      doc.setTextColor(60)
      cx = margin + 2
      const vals = [
        entry.productId,
        entry.productName,
        entry.casNumber,
        entry.substanceName,
        entry.svhcGehalt,
        entry.locationInComponent,
      ]
      for (let i = 0; i < vals.length; i++) {
        const truncated =
          doc.splitTextToSize(vals[i], colWidths[i] - 3)[0] ?? vals[i]
        doc.text(truncated, cx, y)
        cx += colWidths[i]
      }
      y += 6
    })
    y += 6
  }

  // --- 5. Regulatory notes ---
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Regulatorische Hinweise", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  const noteLines = form.regulatoryNotes.split("\n").filter((l) => l.trim())
  for (const noteLine of noteLines) {
    const wrapped = doc.splitTextToSize(noteLine, contentW)
    for (const wl of wrapped) {
      ensureSpace(5)
      doc.text(wl, margin, y)
      y += 4.5
    }
    y += 2
  }
  y += 6

  // --- 6. Signature ---
  ensureSpace(30)
  doc.setDrawColor(80)
  doc.setLineWidth(0.4)
  doc.line(margin, y, margin + 72, y)
  y += 5
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text(form.signatory, margin, y)
  y += 5
  doc.setFont("helvetica", "normal")
  doc.setTextColor(120)
  doc.setFontSize(9)
  doc.text(form.signatoryRole, margin, y)
  y += 5
  doc.text(`${form.signatoryPlace}, ${form.signatoryDate}`, margin, y)

  // --- Footers ---
  addFooter()

  // --- Download ---
  doc.save(`REACH_Konformitaetserklaerung_${sanitizeFilename(customer)}.pdf`)
}

// ---------------------------------------------------------------------------
// Product-focused PDF generation (no customer/Recipient)
// ---------------------------------------------------------------------------

export function generateProductConformityPdf(
  form: ConformityFormData,
  productId: string,
  productName: string,
  svhcRows: SvhcTableRow[],
  isPositive: boolean,
) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentW = pageW - 2 * margin
  let y = margin

  const addFooter = () => {
    const page = doc.getNumberOfPages()
    for (let i = 1; i <= page; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `turnus.ai QMS \u00B7 Vertraulich \u00B7 Seite ${i} von ${page}`,
        margin,
        pageH - 10,
      )
      doc.text(
        `REACH-Konformit\u00E4tserkl\u00E4rung \u00B7 ${productName} \u00B7 ${form.creationDate}`,
        pageW - margin,
        pageH - 10,
        { align: "right" },
      )
    }
  }

  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - 20) {
      doc.addPage()
      y = margin
    }
  }

  // --- 1. Header block ---
  doc.setFontSize(18)
  doc.setTextColor(30)
  doc.setFont("helvetica", "bold")
  doc.text("Konformit\u00E4tserkl\u00E4rung REACH", margin, y)
  y += 7
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(100)
  doc.text(
    "gem\u00E4\u00DF Verordnung (EG) Nr. 1907/2006 (REACH), Artikel 33",
    margin,
    y,
  )
  y += 4
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 2. Meta block (two columns) — product-focused ---
  doc.setFontSize(8)
  doc.setTextColor(120)
  const col1X = margin
  const col2X = margin + contentW / 2 + 5
  const metaLineH = 5

  const metaLeft = [
    ["Aussteller", form.issuer],
    ["Adresse", form.issuerAddress],
    ["Erstellungsdatum", form.creationDate],
  ]
  const metaRight = [
    ["Produkt-ID", productId],
    ["Produktname", productName],
    [
      "Kandidatenliste",
      `${CANDIDATE_LIST_VERSION} (${CANDIDATE_LIST_ENTRIES} Eintr\u00E4ge, ${CANDIDATE_LIST_DATE})`,
    ],
  ]

  for (let i = 0; i < 3; i++) {
    doc.setFont("helvetica", "normal")
    doc.setTextColor(120)
    doc.text(metaLeft[i][0], col1X, y)
    doc.text(metaRight[i][0], col2X, y)
    y += metaLineH * 0.6
    doc.setFont("helvetica", "bold")
    doc.setTextColor(50)
    doc.text(metaLeft[i][1], col1X, y)
    doc.text(metaRight[i][1], col2X, y)
    y += metaLineH
  }

  y += 2
  doc.setDrawColor(200)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 3. Declaration text ---
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Erkl\u00E4rung", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  const declLines = doc.splitTextToSize(form.declarationText, contentW)
  for (const line of declLines) {
    ensureSpace(5)
    doc.text(line, margin, y)
    y += 4.5
  }
  y += 6

  // --- 4. SVHC table (positive only) ---
  if (isPositive && svhcRows.length > 0) {
    ensureSpace(20)
    doc.setFontSize(12)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(30)
    doc.text("SVHC-Stofftabelle", margin, y)
    y += 8

    const colWidths = [36, 38, 36, 60]
    const headers = [
      LABELS.colCasNo,
      LABELS.colSubstance,
      LABELS.colShare,
      LABELS.colLocation,
    ]

    // Header row
    doc.setFillColor(45, 45, 50)
    doc.rect(margin, y - 4, contentW, 7, "F")
    doc.setFontSize(7)
    doc.setFont("helvetica", "bold")
    doc.setTextColor(255)
    let cx = margin + 2
    for (let i = 0; i < headers.length; i++) {
      doc.text(headers[i], cx, y)
      cx += colWidths[i]
    }
    y += 5

    // Data rows — with text wrapping for long rollup values
    doc.setFont("helvetica", "normal")
    doc.setFontSize(7.5)
    const lineH = 3.5 // line height for wrapped text
    svhcRows.forEach((entry, idx) => {
      const vals = [
        entry.casNumber,
        entry.substanceName,
        entry.svhcGehalt,
        entry.locationInComponent,
      ]
      // Pre-compute wrapped lines for each cell to determine row height
      const cellLines = vals.map((v, i) =>
        doc.splitTextToSize(v || "", colWidths[i] - 3),
      )
      const maxLines = Math.max(...cellLines.map((l) => l.length), 1)
      const rowH = Math.max(6, maxLines * lineH + 2)

      ensureSpace(rowH + 2)
      if (idx % 2 === 0) {
        doc.setFillColor(245, 245, 248)
        doc.rect(margin, y - 3.5, contentW, rowH, "F")
      }
      doc.setTextColor(60)
      cx = margin + 2
      for (let i = 0; i < vals.length; i++) {
        let lineY = y
        for (const line of cellLines[i]) {
          doc.text(line, cx, lineY)
          lineY += lineH
        }
        cx += colWidths[i]
      }
      y += rowH
    })
    y += 6
  }

  // --- 5. Regulatory notes ---
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Regulatorische Hinweise", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  const noteLines = form.regulatoryNotes.split("\n").filter((l) => l.trim())
  for (const noteLine of noteLines) {
    const wrapped = doc.splitTextToSize(noteLine, contentW)
    for (const wl of wrapped) {
      ensureSpace(5)
      doc.text(wl, margin, y)
      y += 4.5
    }
    y += 2
  }
  y += 6

  // --- 6. Signature ---
  ensureSpace(30)
  doc.setDrawColor(80)
  doc.setLineWidth(0.4)
  doc.line(margin, y, margin + 72, y)
  y += 5
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text(form.signatory, margin, y)
  y += 5
  doc.setFont("helvetica", "normal")
  doc.setTextColor(120)
  doc.setFontSize(9)
  doc.text(form.signatoryRole, margin, y)
  y += 5
  doc.text(`${form.signatoryPlace}, ${form.signatoryDate}`, margin, y)

  // --- Footers ---
  addFooter()

  // --- Download ---
  doc.save(`REACH_Konformitaetserklaerung_${sanitizeFilename(productName)}.pdf`)
}

// ---------------------------------------------------------------------------
// Escha customer conformity declaration — per-customer product table
// ---------------------------------------------------------------------------

export type EschaKundenTableRow = {
  artikelNr: string
  fremdartikelNr: string
  beschreibung: string
  /** Value of the schema-declared ``kupferanteil_artikel_kg`` formula
   *  primitive ((kupferanteil / je_artikelmenge) * laenge), evaluated
   *  server-side; passed through here as-is. */
  kupferanteilKg: string
  /** Pre-computed client-side by evaluateFormula against the product's
   *  raw primitives (menge_blei * basismenge / gewicht * 100). */
  bleiProzent: string
}

/**
 * Render the Escha customer conformity declaration.  Layout mirrors the
 * reference document: yellow accent rules, two-column meta block with
 * KUNDENNUMMER replacing PRODUKTREFERENZ, REACH/Kupfer boilerplate with
 * monospace formula boxes, a 5-column article overview, regulatory
 * bullets, and a signature row.
 *
 * Helper values are supplied already-evaluated so the PDF generator
 * stays dumb — the caller is responsible for running evaluateFormula
 * against each product's raw primitives.
 */
export function generateEschaKundenKonformitaetPdf(
  customerId: string,
  rows: EschaKundenTableRow[],
  creationDate: string,
) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 18
  const contentW = pageW - 2 * margin
  const YELLOW: [number, number, number] = [253, 224, 71]

  const drawPageChrome = () => {
    // Top yellow rule
    doc.setFillColor(YELLOW[0], YELLOW[1], YELLOW[2])
    doc.rect(0, 0, pageW, 2, "F")
    // Bottom yellow rule
    doc.rect(0, pageH - 10, pageW, 0.6, "F")
  }

  const addFooterAndHeaders = () => {
    const total = doc.getNumberOfPages()
    for (let i = 1; i <= total; i++) {
      doc.setPage(i)
      drawPageChrome()
      doc.setFontSize(8)
      doc.setFont("helvetica", "normal")
      doc.setTextColor(120)
      // Top header strip
      doc.text("Vertraulich · Kundenkonformitätserklärung", margin, 6)
      doc.text(`Seite ${i} von ${total}`, pageW - margin, 6, { align: "right" })
      // Bottom footer strip
      doc.text("Vertraulich · Kundenkonformitätserklärung", margin, pageH - 5)
      doc.text(`Seite ${i} von ${total}`, pageW - margin, pageH - 5, {
        align: "right",
      })
    }
  }

  let y = margin + 4
  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - 18) {
      doc.addPage()
      y = margin + 4
    }
  }

  // --- Title ----------------------------------------------------------------
  doc.setFontSize(22)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(20)
  doc.text("Kundenkonformitätserklärung", margin, y + 4)
  y += 10

  // --- Subtitle -------------------------------------------------------------
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(90)
  const subtitle = doc.splitTextToSize(
    "Material- und Stoffgehaltsprüfung gemäß REACH-Verordnung (EG) Nr. " +
      "1907/2006, Art. 33, sowie Kupfergehaltsprüfung für Zoll- und " +
      "Warenstromdokumentation.",
    contentW,
  )
  for (const line of subtitle) {
    doc.text(line, margin, y)
    y += 4.5
  }
  y += 5

  // --- Meta block (two columns, label+value pairs) -------------------------
  const colW = contentW / 2
  const col1X = margin
  const col2X = margin + colW

  const metaPair = (
    leftLabel: string,
    leftValue: string,
    rightLabel: string,
    rightValue: string,
  ) => {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(8)
    doc.setTextColor(90)
    doc.text(leftLabel, col1X, y)
    doc.text(rightLabel, col2X, y)
    y += 4
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)
    doc.setTextColor(20)
    doc.text(leftValue, col1X, y)
    doc.text(rightValue, col2X, y)
    y += 6
  }

  metaPair("AUSSTELLER", "turnus GmbH", "ERSTELLUNGSDATUM", creationDate)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(20)
  doc.text("Musterstraße 42, 80331 München", col1X, y)
  y += 7
  metaPair(
    "KUNDENNUMMER",
    customerId || "—",
    "KANDIDATENLISTE (REACH)",
    `${CANDIDATE_LIST_VERSION} (${CANDIDATE_LIST_ENTRIES} Einträge, ${CANDIDATE_LIST_DATE})`,
  )
  y += 2

  // --- Section helpers ------------------------------------------------------
  const sectionHeading = (text: string) => {
    ensureSpace(10)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(12)
    doc.setTextColor(20)
    doc.text(text, margin, y + 2)
    y += 8
  }

  const bodyText = (text: string) => {
    doc.setFont("helvetica", "normal")
    doc.setFontSize(9.5)
    doc.setTextColor(55)
    const lines = doc.splitTextToSize(text, contentW)
    for (const line of lines) {
      ensureSpace(5)
      doc.text(line, margin, y)
      y += 4.5
    }
    y += 2
  }

  const formulaBox = (text: string) => {
    ensureSpace(9)
    doc.setFillColor(245, 245, 245)
    doc.rect(margin, y - 4, contentW, 7, "F")
    doc.setFont("courier", "bold")
    doc.setFontSize(9)
    doc.setTextColor(20)
    doc.text("  " + text, margin + 2, y + 1)
    y += 7
  }

  // --- Section 1 ------------------------------------------------------------
  sectionHeading("1. Erklärung zum Kupfergehalt")
  bodyText(
    "Der Kupfergehalt je Artikel wird aus den hinterlegten Stammdaten " +
      "(Kupfergehalt je Referenzlänge, Artikellänge) automatisiert durch " +
      "das turnus.ai QMS berechnet und dient der Zolltarif-Dokumentation, " +
      "der Warenursprungsbestimmung sowie internen Materialkalkulationen. " +
      "Die Berechnung erfolgt je Einheit nach folgender Formel:",
  )
  formulaBox(
    "Kupferanteil je Artikel [kg] = (Kupfergehalt ÷ Referenzmenge) × Artikellänge",
  )
  bodyText(
    "Die angegebenen Werte gelten je Einzelstück des jeweiligen Artikels. " +
      "Alle Eingabewerte sind durch Lieferantendokumentation bzw. " +
      "ERP-Stammdaten belegt und werden im QMS auditfähig dokumentiert.",
  )

  // --- Section 2 ------------------------------------------------------------
  sectionHeading("2. Erklärung zum SVHC-Gehalt gemäß REACH Art. 33")
  bodyText(
    "Gemäß Art. 33 Abs. 1 der Verordnung (EG) Nr. 1907/2006 (REACH) " +
      "sind Lieferanten von Erzeugnissen zur Mitteilungspflicht " +
      "verpflichtet, sobald ein auf der REACH-Kandidatenliste geführter " +
      "besonders besorgniserregender Stoff (SVHC) in einer Konzentration " +
      "von mehr als 0,1 Gew.-% (w/w) im Erzeugnis enthalten ist. " +
      "Informationen zur Stoffidentität und zur sicheren Verwendung sind " +
      "dem gewerblichen Abnehmer unaufgefordert, Verbrauchern auf Anfrage " +
      "innerhalb von 45 Tagen zur Verfügung zu stellen. Unabhängig davon " +
      "gilt gemäß Art. 7 Abs. 2 eine Mitteilungspflicht gegenüber der " +
      "ECHA, sobald die SVHC-Gesamtmenge je Hersteller/Importeur 1 " +
      "t/Jahr überschritten wird.",
  )
  bodyText(
    "Der gewichtsbezogene SVHC-Anteil je Artikel wird aus der " +
      "hinterlegten SVHC-Masse und dem Gesamtgewicht des Artikels wie " +
      "folgt berechnet:",
  )
  formulaBox("SVHC % (w/w) = (Menge SVHC × Basismenge ÷ Gewicht) × 100")
  bodyText(
    "Werte oberhalb der 0,1-%-Schwelle lösen automatisch die " +
      "Mitteilungspflicht gemäß Art. 33 aus. Die zugrunde liegenden " +
      "Stammdaten — Menge Blei (U), Basismenge (M), Gewicht (K) — werden " +
      "je Datensatz auditfähig dokumentiert.",
  )

  // --- Section 3: 5-column product table -----------------------------------
  sectionHeading("3. Artikelübersicht mit berechneten Konformitätswerten")

  // Column widths sum to contentW.  Layout favours Beschreibung since it
  // often wraps; numeric columns are narrow.
  const tableCols: { label: string; width: number }[] = [
    { label: "Artikel-Nr.", width: 26 },
    { label: "Fremd-\nartikel-Nr.", width: 28 },
    { label: "Beschreibung", width: 62 },
    { label: "Kupfer-\nanteil (kg)", width: 28 },
    { label: "Blei % (w/w)", width: contentW - 26 - 28 - 62 - 28 },
  ]

  const drawTableHeader = () => {
    const headerH = 11
    ensureSpace(headerH + 2)
    doc.setFillColor(238, 238, 238)
    doc.rect(margin, y - 4, contentW, headerH, "F")
    doc.setDrawColor(190)
    doc.setLineWidth(0.3)
    doc.line(margin, y - 4, margin + contentW, y - 4)
    doc.line(margin, y - 4 + headerH, margin + contentW, y - 4 + headerH)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(8.5)
    doc.setTextColor(20)
    let cx = margin + 2
    for (const c of tableCols) {
      const labelLines = c.label.split("\n")
      let ly = y
      for (const l of labelLines) {
        doc.text(l, cx, ly)
        ly += 3.5
      }
      cx += c.width
    }
    y += headerH
  }

  drawTableHeader()

  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  const lineH = 3.8
  const rowPad = 3
  rows.forEach((r, idx) => {
    const vals = [
      r.artikelNr,
      r.fremdartikelNr,
      r.beschreibung,
      r.kupferanteilKg,
      r.bleiProzent,
    ]
    const cellLines = vals.map((v, i) =>
      doc.splitTextToSize(v || "—", tableCols[i].width - 3),
    )
    const rowH = Math.max(
      7,
      Math.max(...cellLines.map((l) => l.length)) * lineH + rowPad,
    )

    if (y + rowH > pageH - 18) {
      doc.addPage()
      y = margin + 4
      drawTableHeader()
    }

    if (idx % 2 === 1) {
      doc.setFillColor(250, 250, 250)
      doc.rect(margin, y - 3, contentW, rowH, "F")
    }
    doc.setDrawColor(220)
    doc.setLineWidth(0.2)
    doc.line(margin, y + rowH - 3, margin + contentW, y + rowH - 3)

    doc.setTextColor(45)
    let cx = margin + 2
    for (let i = 0; i < vals.length; i++) {
      let ly = y
      for (const line of cellLines[i]) {
        doc.text(line, cx, ly)
        ly += lineH
      }
      cx += tableCols[i].width
    }
    y += rowH
  })
  y += 3

  // --- Legende --------------------------------------------------------------
  bodyText(
    "Legende: Artikel mit Blei % (w/w) > 0,1 sind gemäß REACH Art. 33 " +
      "mitteilungspflichtig. Die Mitteilung an gewerbliche Kunden erfolgt " +
      "automatisiert über die QMS-Schnittstelle. Abweichungen zwischen " +
      "den Stammdaten im Legacy-ERP und dem berechneten Wert werden zur " +
      "Prüfung gesondert ausgewiesen.",
  )

  // --- Section 4: regulatory bullets ---------------------------------------
  sectionHeading("4. Regulatorische Hinweise")

  const regulatoryItem = (lead: string, rest: string) => {
    ensureSpace(6)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9.5)
    doc.setTextColor(20)
    const leadText = `${lead} — `
    const leadWidth = doc.getTextWidth(leadText)
    doc.text(leadText, margin, y)
    doc.setFont("helvetica", "normal")
    doc.setTextColor(55)
    const restLines = doc.splitTextToSize(rest, contentW - leadWidth)
    if (restLines.length > 0) {
      doc.text(restLines[0], margin + leadWidth, y)
      for (let i = 1; i < restLines.length; i++) {
        y += 4.5
        ensureSpace(5)
        doc.text(restLines[i], margin, y)
      }
    }
    y += 6
  }

  regulatoryItem(
    "REACH (EG) Nr. 1907/2006, Art. 33",
    "Mitteilungspflicht für SVHC > 0,1 Gew.-% erfüllt.",
  )
  regulatoryItem(
    "REACH Art. 7 Abs. 2",
    "Mitteilungspflicht geprüft; Mengenschwelle von 1 t/Jahr je Stoff " +
      "wird auf Herstellerebene überwacht.",
  )
  regulatoryItem(
    "RoHS 2011/65/EU",
    "Stoffverbote und aktuelle Ausnahmeliste (Anhang III/IV) geprüft.",
  )
  regulatoryItem(
    "SCIP-Datenbank",
    "meldepflichtige Artikel werden mit SCIP-ID im QMS erfasst und an " +
      "die ECHA übermittelt.",
  )
  y += 4

  // --- Signature ------------------------------------------------------------
  ensureSpace(22)
  doc.setDrawColor(180)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 5
  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(20)
  doc.text("Dr. Max Mustermann", col1X, y)
  doc.text(`München, ${creationDate}`, col2X, y)
  y += 5
  doc.setFontSize(9)
  doc.setTextColor(120)
  doc.text("Compliance Manager · turnus GmbH", col1X, y)
  doc.text("Dokument automatisch generiert durch turnus.ai QMS", col2X, y)

  // --- Chrome + save --------------------------------------------------------
  addFooterAndHeaders()
  doc.save(
    `Kundenkonformitaetserklaerung_${sanitizeFilename(customerId || "kunde")}.pdf`,
  )
}
